import { useEffect, useState } from "react"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import type { E164Number } from "libphonenumber-js/types.cjs"
import { postJson } from "../api"
import JoinCard from "../components/JoinCard"
import { Step, pathForStep, stepFromPath } from "../joinSteps"
import { clearReferCode, currentReferCode } from "../referCode"
import CodeStep from "../steps/CodeStep"
import DoneStep from "../steps/DoneStep"
import PhoneStep from "../steps/PhoneStep"
import type { Profile } from "../steps/ProfileStep"
import ProfileStep from "../steps/ProfileStep"
import useFetch from "../useFetch"
import HomePage from "./HomePage"

interface ReferrerResponse {
  name: string
}

export default function LandingPage() {
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const [referCode] = useState(currentReferCode)
  const [referrer, loading, failure] = useFetch<ReferrerResponse>(
    referCode ? `/join/${referCode}` : null
  )

  const [error, setError] = useState("")
  const [phone, setPhone] = useState<E164Number>()
  const [phoneMask, setPhoneMask] = useState("")
  const [token, setToken] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [approved, setApproved] = useState(false)

  const step = stepFromPath(pathname)

  // Moving between steps, including with the back button, shouldn't carry over
  // an error from the step you just left
  useEffect(() => setError(""), [pathname])

  // A bad code would otherwise wedge this browser on the error screen forever.
  // Only a definite 404 counts; a blip mustn't throw away a good invite.
  useEffect(() => {
    if (failure === "not-found") clearReferCode()
  }, [failure])

  const goTo = (next: Step, replace = false) => navigate(pathForStep(next, referCode), { replace })

  const begin = async () => {
    const res = await postJson("/join/begin", { phone, refer_code: referCode })

    if (!res.success) {
      setError(res.message)
      return false
    }

    setPhoneMask(res.message)
    // Resending from the code step shouldn't stack up history entries
    goTo(Step.Code, step === Step.Code)
    return true
  }

  const verify = async (code: string) => {
    const res = await postJson("/join/verify", { phone, code })

    if (!res.success) {
      setError(res.message)
      return
    }

    setToken(res.message)
    goTo(Step.Profile)
  }

  const submit = async (details: Profile) => {
    const res = await postJson<{ approved: boolean }>("/join/submit", {
      token,
      refer_code: referCode,
      ...details,
    })

    if (!res.success) {
      setError(res.message)
      return
    }

    // The invite has been redeemed, so don't leave it sitting in this browser
    clearReferCode()
    setApproved(!!res.data?.approved)
    setSubmitted(true)
    goTo(Step.Done, true)
  }

  if (!referCode) {
    return <HomePage />
  }

  if (loading) {
    return (
      <div className="join-page">
        <div className="spinner-border text-secondary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (failure === "error") {
    // The code is still in the URL, so a reload simply retries the lookup
    return (
      <div className="join-page">
        <JoinCard title="Something went wrong" subtitle="We couldn't load your invite.">
          <button
            className="btn btn-primary btn-lg w-100"
            type="button"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </JoinCard>
      </div>
    )
  }

  if (failure === "not-found" || !referrer) {
    return (
      <div className="join-page">
        <JoinCard title="This invite link is invalid" subtitle="Ask your friend for a new one." />
      </div>
    )
  }

  // The flow lives in memory, so a refresh or a deep link starts over. The
  // invite code is carried into the restart so a forwarded link still credits
  // the referrer.
  const restart = <Navigate to={pathForStep(Step.Phone, referCode)} replace />

  if (step === null) return restart
  if (submitted && step !== Step.Done) {
    return <Navigate to={pathForStep(Step.Done, referCode)} replace />
  }
  if (step === Step.Done && !submitted) return restart
  if (step === Step.Code && !phoneMask) return restart
  if (step === Step.Profile && !token) return restart

  return (
    <div className="join-page">
      {step === Step.Phone && (
        <PhoneStep
          referrerName={referrer.name}
          phone={phone}
          onChange={(value) => {
            setPhone(value)
            setError("")
          }}
          onSubmit={begin}
          error={error}
        />
      )}

      {step === Step.Code && (
        <CodeStep
          phoneMask={phoneMask}
          onSubmit={verify}
          onResend={begin}
          onBack={() => goTo(Step.Phone)}
          error={error}
          clearError={() => setError("")}
        />
      )}

      {step === Step.Profile && (
        <ProfileStep onSubmit={submit} error={error} clearError={() => setError("")} />
      )}

      {step === Step.Done && <DoneStep approved={approved} />}
    </div>
  )
}
